import router from "next/router";
import { Logger } from "common/middleware/logger";
import { ApiClientFactory } from "common/middleware/api.client-factory";
import {
  ClientDto,
  CreateClientCommand,
  CreateNoteCommand,
  EditClientCommand,
  EditNoteCommand,
  ICreateNoteCommand,
  IEditNoteCommand,
  NoteDto,
  PaginatedResultOfClientLookupDto,
  PaginatedResultOfContactDto,
  PaginatedResultOfDealLookupDto,
  DealState,
} from "./api.services";
import {
  ApiError,
  ApiValidationError,
  callApiAndHandleExpectedApiExceptions,
} from "./api.helpers";
import {
  mutateClient,
  mutateClientNotes,
  mutateClients
} from "./hooks";

export const createClient = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  projectId: number,
  language: string,
): Promise<ClientDto | undefined | ApiValidationError>  => {
  Logger.debug("Adding Client ", firstName);

  const client = await callApiAndHandleExpectedApiExceptions(async () => {
    let clientsClient = await ApiClientFactory.getClientsClient();
    return await clientsClient.create(
      CreateClientCommand.fromJS({
        firstName,
        lastName,
        email,
        phoneNumber,
        projectId,
        language,
      })
    );
  }, []);

  if (client instanceof ApiValidationError) {
    if (client.errors["Email"]) {
      throw { type: "email", msg: "Failed to create client because the email is already in use" }
    }
    else {
      throw { type: "validation", msg: "Failed to validate new client information" }
    }
  } else if (!client || client instanceof ApiError || client === undefined) {
    throw { type: "error", msg: "Failed to create new client" }
  }

  mutateClients(
    router.query.page as string,
    router.query.pageSize as string,
    router.query.projectId as string,
    router.query.search as string,
  );

  return client;
};

export const getClients = async (
  key: string,
  page?: string,
  pageSize?: string,
  projectId?: string,
  searchString?: string,
): Promise<PaginatedResultOfClientLookupDto> => {
    let client = await ApiClientFactory.getClientsClient();

    const clients = await client.getPage(
      projectId ? Number(projectId) : null,
      [],
      "asc",
      searchString,
      page ? Number(page) : null,
      true,
      pageSize ? Number(pageSize) : null,
      true
    );
    return clients;
  };

export const getClient = async (
  key: string,
  clientId: number
): Promise<ClientDto> => {
    let client = await ApiClientFactory.getClientsClient();

     return await client.get(
      clientId
    );
  };

export const editClient = async (
  clientId: number,
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  projectId: number,
  language: string,
): Promise<string | ApiValidationError>  => {
  Logger.debug("Editing Client ", clientId);

  const client = await callApiAndHandleExpectedApiExceptions(async () => {
    let clientsClient = await ApiClientFactory.getClientsClient();
      return await clientsClient.edit(
        clientId,
        EditClientCommand.fromJS({
          id: clientId,
          firstName,
          lastName,
          email,
          phoneNumber,
          projectId,
          language,
      })
    );
  }, []);

  if (client instanceof ApiValidationError) {
    if (client.errors["Email"]) {
      throw { type: "email", msg: "Failed to edit client because new email is already in use" }
    }
    else {
      throw { type: "validation", msg: "Failed to validate client information" }
    }
  } else if (client instanceof ApiError) {
    throw { type: "error", msg: "Failed to edit client" }
  }

  mutateClient(clientId);

  return client;
};

export const createClientNote = async (
  clientId: number,
  body: ICreateNoteCommand,
) => {
  Logger.debug("Creating note for client", clientId);

  const client = await ApiClientFactory.getClientsClient();

  await client.createClientNote(
    clientId,
    new CreateNoteCommand({
      noteType: body.noteType,
      createdById: body.createdById,
      description: body.description,
      subject: body.subject,
      projectId: body.projectId,
      dateTimeOfNoteUtc: body.dateTimeOfNoteUtc,
      languageType: body.languageType,
      builderClientId: body.builderClientId,
      listingId: body.listingId,
    })
  );

  mutateClientNotes(clientId, router.query.projectId as string);
}

export const editClientNote = async (
  clientId: number,
  noteId: string,
  body: IEditNoteCommand,
) => {
  Logger.debug(`Editing client note: clientId ${clientId} noteId ${noteId}`);

  const client = await ApiClientFactory.getClientsClient();

  await client.editClientNote(
    clientId,
    noteId,
    new EditNoteCommand({
      id: noteId,
      description: body.description,
      subject: body.subject,
      projectId: body.projectId,
      dateTimeOfNoteUtc: body.dateTimeOfNoteUtc,
      builderClientId: body.builderClientId,
      listingId: body.listingId,
    })
  );

  mutateClientNotes(clientId, router.query.projectId as string);
}

export const getClientNotes = async (
  key: string,
  clientId: number,
  filterByProjectId: string,
): Promise<NoteDto[]> => {

  const client = await ApiClientFactory.getClientsClient();

  return await client.getClientNotes(
    clientId,
    [Number(filterByProjectId)],
  );
};

export const getClientPurchaseOffers = async (
  key: string,
  clientId: number,
  filterByProjectId: number[],
  page: string,
  pageSize: string,
): Promise<PaginatedResultOfDealLookupDto> => {

  const client = await ApiClientFactory.getClientsClient();

  return await client.getDeals(
    clientId,
    [
      DealState.PendingSellerSignature,
      DealState.Accepted,
      DealState.Cancelled,
      DealState.Firm,
    ],
    filterByProjectId,
    undefined,
    "state",
    undefined,
    page ? Number(page) : null,
    true,
    pageSize ? Number(pageSize) : null,
    !page || !pageSize,
  );
};

export const getClientContacts = async (
  key: string,
  clientId: number,
  page: string,
  pageSize: string,
): Promise<PaginatedResultOfContactDto> => {

  const client = await ApiClientFactory.getClientsClient();
  return await client.getContacts(
    clientId,
    "asc",
    "",
    page ? Number(page) : null,
    true,
    pageSize ? Number(pageSize) : null,
    true
  );
};